/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import {
  BundleTypeKind,
  IBundle,
  IBundle_Entry,
  IBundle_Link,
  ICondition,
  IDocumentReference,
  IMedicationRequest,
  IObservation,
  IOperationOutcome,
  IPatient,
  IProcedure,
  IReference,
} from '@ahryman40k/ts-fhir-types/lib/R4';

import pearlApi from '../api/pearlApi';
import {
  combineDiagnosticReportsWithObservations,
  convertFhirRulesToListOfCPTCodes,
  escapeFhir,
  getPatientMRN,
} from './fhir_utils';
import { snackNotifications } from '../notification/SnackBarUtils';
import { FhirWidgetBase, FhirPredefinedWidget, GroupedObservationType } from '../algorithm/types';
import {
  IDiagnosticReportSimplified,
  IObservationSimplified,
  diagnosticReport_elements_str,
  IObservationBundle,
  IConditionBundle,
  IMedicationRequestBundle,
  IDiagnosticReportBundleSimplified,
  IProcedureBundle,
} from './fhir_types';
import { analyticsTrack } from '../analytics_utils';
import { sort_unique } from '../utils';
import { PatientContextType } from '../Login/types';
import { showPatientProblemListAndPastMedicalHx } from '../permissions';
import { adjustTestPatientDates } from '../TestPatients/adjustTestPatientDates';

let diagnosticReportsCache: { [id: string]: IDiagnosticReportSimplified[] } = {};

type FhirConfigWithHeaders = {
  headers: {
    'X-Pearl-MRN': string;
    'X-Pearl-FHIR-Servers'?: string;
    'Content-Type'?: string;
  };
  signal?: AbortSignal;
};

export function clearDiagnosticReportsCache() {
  diagnosticReportsCache = {};
}

function isFailureResponse(res: IOperationOutcome | IBundle): boolean {
  if (res.resourceType === 'OperationOutcome') {
    return true;
  }
  if (res.resourceType === 'Bundle') {
    return res.total === 0 && res?.entry?.[0]?.resource?.resourceType === 'OperationOutcome';
  }
  return false;
}

export async function makePearlApiFhirCallAndUnwrap<T>(
  path: string,
  mrn: string,
  server: string,
  signal?: AbortSignal,
  header: Record<string, string> = {}
): Promise<(T & { server: string }) | undefined> {
  const fhirConfig: FhirConfigWithHeaders = {
    headers: {
      'X-Pearl-MRN': mrn,
      'X-Pearl-FHIR-Servers': server,
      ...header,
    },
    signal,
  };
  const res = await pearlApi.get<
    | {
        data: { server: 'orca' | 'wamt' | 'ak' | 'musc'; response?: T & IOperationOutcome }[];
      }
    | {
        // When there's an internal server error in medpearl, we get this format instead
        errors: { status: number; title: string }[];
      }
  >(path, fhirConfig);
  const data = res?.data;

  if (typeof data !== 'object' || Array.isArray(data)) {
    return data as any; // Binary/  etc returns a string
  }

  // console.log('data is', res?.data?.data[0]?.response);
  if (data && 'errors' in data) {
    console.error('Error fetching data. path:', path, 'errors:', data.errors);
    throw new Error('Error fetching data: ' + data.errors.map((e) => `${e.status}: ${e.title}`).join(', '));
  }
  const dataFromAllServers = data?.data; // Array of { server, response }
  // If we have multiple responses, filter out the OperationOutcome ones then pick the first.  If all are OperationOutcome, just pick the first.
  // In practice this should ONLY happen when we're querying multiple servers (server='*') which only happens when querying
  // Patient/identifier=XXX and that only happens when the user is manually entering an MRN.
  // After we have a patient, we always query that patient's specific server. And when coming from epic, we have an identifier and don't need to
  // query by MRN.
  const bestServer =
    dataFromAllServers?.find((x) => !isFailureResponse(x.response as IOperationOutcome | IBundle)) ||
    dataFromAllServers?.[0];
  const response = bestServer?.response;
  const ret = response as (typeof response & { server: string }) | undefined; // We're going to add the server to the response
  // Response might be a string (if it's xml) or an object (if it's json). Add server if it's an object
  if (ret && typeof ret === 'object') {
    ret.server = bestServer?.server || 'unknown';
    // Check if we have an OperationOutcome issue
    if (ret?.resourceType === 'OperationOutcome' && ret?.issue) {
      console.error('Error fetching data. path:', path, 'issue:', ret.issue);
      const fatalIssue = ret.issue.find((i) => i.severity === 'fatal');
      if (fatalIssue) {
        snackNotifications.error('Fatal error fetching data: ' + fatalIssue.details?.text);
        throw new Error('Fatal error fetching data: ' + fatalIssue.details?.text);
      }
    }
  }

  return ret;
}

/** Same as makePearlFhirCallAndUnwrap but uses POST instead of GET */
export async function makePearlApiFhirPostCallAndUnwrap<T>(
  path: string,
  data: object,
  mrn: string,
  server: string,
  signal?: AbortSignal,
  header: Record<string, string> = {}
): Promise<(T & { server: string }) | undefined> {
  const fhirConfig: FhirConfigWithHeaders = {
    headers: {
      'X-Pearl-MRN': mrn,
      'X-Pearl-FHIR-Servers': server,
      'Content-Type': 'application/fhir+json',
      ...header,
    },
    signal,
  };
  const res = await pearlApi.post<{
    data: { server: 'orca' | 'wamt' | 'ak' | 'musc'; response?: T & IOperationOutcome }[];
  }>(path, data, fhirConfig);

  // console.log('data is', res?.data?.data[0]?.response);
  const response = res?.data?.data[0]?.response;
  const ret = response as (typeof response & { server: string }) | undefined; // We're going to add the server to the response
  // Response might be a string (if it's xml) or an object (if it's json). Add server if it's an object
  if (ret && typeof ret === 'object') {
    ret.server = res?.data?.data[0]?.server || 'unknown';

    // Check if we have an OperationOutcome issue
    if (ret?.resourceType === 'OperationOutcome' && ret?.issue) {
      console.error('Error fetching data. path:', path, 'issue:', ret.issue);
      if (ret.issue[0]?.severity === 'fatal') {
        snackNotifications.error('Fatal error fetching data: ' + ret.issue[0]?.details?.text);
        throw new Error('Fatal error fetching data: ' + ret.issue[0]?.details?.text);
      }
    }
  }

  return ret;
}

/**
 * This will add "&_count=${pageSize}" to the path, and will half the pageSize if it fails, a maximum of 3 times.
 */
export async function makePearlApiFhirCallAndUnwrapPaged<T extends IBundle & { type: BundleTypeKind._searchset }>(
  path: string,
  mrn: string,
  server: string,
  signal?: AbortSignal,
  newResultsCallback?: (res: T) => void,
  combinedResults?: T,
  pageSize = 64
): Promise<T | undefined> {
  let doRetry = true;
  try {
    let link: IBundle_Link[] | undefined;
    {
      if (signal?.aborted) return undefined;
      let result = await makePearlApiFhirCallAndUnwrap<T>(`${path}&_count=${pageSize}`, mrn, server, signal);
      if (!result) return;
      // If the result is a string, it's an error from the server like <html>..</html>
      if (typeof result === 'string') {
        doRetry = false;
        throw new Error('Server returned an error: ' + result);
      }
      if (combinedResults) {
        if (!combinedResults?.entry) combinedResults.entry = [];
        combinedResults.entry = combinedResults.entry.concat(result?.entry || []);
      } else {
        combinedResults = result;
      }
      // If there are more pages, get those too:
      link = result?.link;
      delete result?.link; // Just to clean it up
    }
    newResultsCallback?.(combinedResults);

    do {
      if (signal?.aborted) return undefined;
      const next = link?.find((l: any) => l.relation === 'next');
      if (next?.url) {
        // url will be like:
        // https://haikuor-staging.providence.org/fhirproxy/api/FHIR/R4/DiagnosticReport?sessionID=23-0EC5CEEC004411EE9E4DC301D81100B2
        // We need to strip off the start, leaving just the /fhir/DiagnosticReport?sessionID=23-0EC5CEEC004411EE9E4DC301D81100B2
        const url = next.url.replace(/^.*\/FHIR\/R4/, '/fhir');
        const res = await makePearlApiFhirCallAndUnwrap<T>(url, mrn, server, signal);
        combinedResults.entry = (combinedResults.entry || []).concat(res?.entry || []);
        combinedResults.total = ((combinedResults.total ?? combinedResults.entry?.length) || 0) + (res?.total || 0); // We don't really use the total, but just for the sake of it
        newResultsCallback?.(combinedResults);
        link = res?.link;
      } else {
        break;
      }
    } while (true);
    if (signal?.aborted) return undefined;

    // Return the first server response, since we're only querying a single server
    return combinedResults;
  } catch (e) {
    if (doRetry) console.warn('Failed to fetch paged results with pageSize', pageSize, e);
    if (doRetry && pageSize > 16) {
      pageSize = pageSize / 2;
      console.log('Retrying with smaller pageSize', pageSize);
      snackNotifications.warning('Patient server is slow.. retrying, sorry..');
      return await makePearlApiFhirCallAndUnwrapPaged(
        path,
        mrn,
        server,
        signal,
        newResultsCallback,
        combinedResults,
        pageSize
      );
    } else {
      throw e;
    }
  }
}

export async function makePearlApiFhirCallWrapper(
  str: string,
  mrn: string | null,
  server: string | undefined,
  addCount = true
) {
  if (addCount && str.includes('?')) {
    return await makePearlApiFhirCallAndUnwrapPaged('/fhir/' + str, mrn || 'testing_fake_mrn', server || '*');
  } else {
    // Don't use the paged version for single requests like Binary/xxx
    return await makePearlApiFhirCallAndUnwrap('/fhir/' + str, mrn || 'testing_fake_mrn', server || '*');
  }
}

/** This function is used to either fetch all diagnostic reports (if cptList is not set) or
 *  fetch only the diagnostic reports that are in the list of CPT codes.
 *  It is an optimization on fetchDiagnosticReports, which fetches all diagnostic reports.
 */
export async function fetchDiagnosticReportsOnlyRequired(
  fhirPatientId: string,
  mrn: string,
  server: string,
  signal: AbortSignal,
  config?: FhirWidgetBase,
  testAndSetLoading?: (cptOrName: string) => boolean, // Callback called for each cpt code that is fetched.  Returns false if already fetched/loading, to mean don't load again!
  newResultsCallback?: (results: IDiagnosticReportSimplified[]) => void // Callback call continually as each page of results is fetched
): Promise<IDiagnosticReportSimplified[] | undefined> {
  // First get the list of CPT codes that we need from the config
  let { cptList, nameList } = convertFhirRulesToListOfCPTCodes(config?.rules);
  if (testAndSetLoading) {
    cptList = cptList?.filter((cpt) => testAndSetLoading(cpt));
    nameList = nameList?.filter((cpt) => testAndSetLoading(cpt));
  }

  console.log('cptList', cptList);
  const res = await fetchDiagnosticReports(
    fhirPatientId,
    mrn,
    server,
    signal,
    newResultsCallback,
    cptList?.join(','),
    nameList?.map(escapeFhir).join(',')
  );
  return res;
}

/**
 *  Fetches the patient's current active problem list
 *
 *  Requires the permissions:
 *   - Condition.Search (Problems) (R4): /api/FHIR/R4/Condition?category=problem-list-item&clinical-status=active - https://fhir.epic.com/Specifications?api=953
 */
export async function fetchProblemList(
  fhirPatientId: string,
  mrn: string,
  server: string,
  signal: AbortSignal,
  newResultsCallback: (problemList: ICondition[] | undefined) => void
) {
  const problemListRaw = await makePearlApiFhirCallAndUnwrap<IConditionBundle>(
    `/fhir/Condition?_elements=code,resourceType,presentedForm,recordedDate,severity&category=problem-list-item&patient=${fhirPatientId}&clinical-status=active`,
    mrn,
    server,
    signal
  );
  if (signal?.aborted) return;
  const problemList = problemListRaw?.entry
    ?.map((e) => e.resource as ICondition)
    .filter((e) => e.resourceType === 'Condition')
    .sort((a, b) => new Date(b.recordedDate || '').getTime() - +new Date(a.recordedDate || '').getTime());
  // console.log('problem-list', problemList, problemListRaw);
  if (problemList && problemList.length > 0) {
    newResultsCallback(problemList);
  }
}

/**
 *  Fetches the patient's current active medications, and their vitals for the last 24 hours
 *  Returns the medications and vitals in multiple callback calls to speed up the UI.
 *
 *  This requires the permissions:
 *   - Observation.Search (Vital Signs) (R4) - https://fhir.epic.com/Specifications?api=973
 *   - MedicationRequest.Search (Signed Medication Order) - https://fhir.epic.com/Specifications?api=997
 */
export async function fetchMedicinesVitals(
  fhirPatientId: string,
  mrn: string,
  server: string,
  signal: AbortSignal,
  newResultsCallback: (
    medicines: IMedicationRequest[] | undefined,
    vitals: IObservationSimplified[] | undefined
  ) => void
) {
  // Fetch the last 30 days of vitals, and limit to the most recent 10 vitals.
  const vitalsRaw = await makePearlApiFhirCallAndUnwrap<IObservationBundle>(
    `/fhir/Observation?category=vital-signs&patient=${fhirPatientId}&count=10&date=gt${
      new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 30).toISOString().slice(0, -5) + 'Z'
    }`,
    mrn,
    server,
    signal
  );
  if (signal?.aborted) return;
  const vitals = vitalsRaw?.entry?.map((e) => e.resource as IObservationSimplified).filter((e) => !!e);
  // console.log('vitals', vitals, vitalsRaw);
  if (vitals && vitals.length > 0) {
    newResultsCallback(undefined, vitals);
  }
  /** Athena requires intent=order and we want only active medications */
  const medicinesRaw = await makePearlApiFhirCallAndUnwrap<IMedicationRequestBundle>(
    `/fhir/MedicationRequest?patient=${fhirPatientId}&status=active&intent=order`,
    mrn,
    server,
    signal
  );
  if (signal?.aborted) return;
  const medicines = medicinesRaw?.entry?.map((e) => e.resource as IMedicationRequest).filter((e) => !!e);
  // console.log('medicines', medicines, medicinesRaw);
  if (medicines && medicines.length > 0) {
    newResultsCallback(medicines, undefined);
  }
}

export const fetchDiagnosticReports = fetchDiagnosticReportsTimed;

/**
 *  Timing results:
 *   - Times for 20000000006, 20000000007, 20000000008
 *   - Unoptimized: (3.9s, 4.2s), (9.3s, 10.5s), (24.2s, 21.4s, 17.7s)
 */
export async function fetchDiagnosticReportsTimed(
  fhirPatientId: string,
  mrn: string,
  server: string,
  signal: AbortSignal,
  newResultsCallback?: (results: IDiagnosticReportSimplified[]) => void, // Callback call continually as each page of results is fetched
  fitlerCptCodes?: string, // If provided, only fetch diagnostic reports that match these CPT codes
  filterNameList?: string // If provided, only fetch diagnostic reports that match these names
): Promise<IDiagnosticReportSimplified[] | undefined> {
  const start = Date.now();
  const res = await fetchDiagnosticReports_(
    fhirPatientId,
    mrn,
    server,
    signal,
    newResultsCallback,
    fitlerCptCodes,
    filterNameList
  );
  const end = Date.now();
  const time_s = (end - start) / 1000;
  console.log('fetchDiagnosticReports took', time_s, 'seconds');
  analyticsTrack('timing', { seconds: time_s, name: 'fetchDiagnosticReports' }, undefined);
  return res;
}

/** This function fetches ALL diagnostic reports.  But this can take ages, even without the
 *  observations.  5 seconds on my fast internet.
 *
 *  See specification here:
 *  https://fhir.epic.com/Specifications?api=989
 *  Requires the permissions:
 *  - DiagnosticReport.Search (Results) (R4): /api/FHIR/R4/DiagnosticReport?patient=<patient_id> - https://fhir.epic.com/Specifications?api=989
 */
async function fetchDiagnosticReports_(
  fhirPatientId: string,
  mrn: string,
  server: string,
  signal: AbortSignal,
  newResultsCallback?: (results: IDiagnosticReportSimplified[]) => void, // Callback call continually as each page of results is fetched
  filterCptCodes?: string, // If provided, only fetch diagnostic reports that match these CPT codes
  filterNameList?: string // If provided, only fetch diagnostic reports that match these names
): Promise<IDiagnosticReportSimplified[] | undefined> {
  /* By default, DiagnosticReport records only include references to the individual result records.
  The result details can be subsequently read via the Observation endpoint. As a convenience,
  it is possible to include those details in the DiagnosticReport resource by using the
  _include parameter with the Observation resource type. For example, the following query
  will return a DiagnosticReport resource with the details of the referenced Observation
  resources included in the contained property:

  GET DiagnosticReport?_id=emeYpBSQZ8J0g7NLblm5LqA3&_include=DiagnosticReport:result:Observation

  See specification here:
  https://fhir.epic.com/Specifications?api=989

  */

  const cached = diagnosticReportsCache[fhirPatientId];
  if (cached) {
    newResultsCallback?.(cached);
    return cached;
  }

  const pageSize = 100;

  // It is not currently possible to sort by date, so we filter to get first the last 3 months,
  // then 1 year, then jump back 1 year at a time for 5 years, then get the rest.
  // To implement this, make an array with what we want to filter by, and then
  // loop through it.
  const filters = [];
  if (filterCptCodes) {
    filters.push(`&code=${filterCptCodes}`);
  } else {
    const date = new Date();
    // 3 months ago from today's date
    date.setMonth(date.getMonth() - 3);
    let dateStr_gt = date.toISOString().slice(0, 19) + 'Z'; // Need the full timezone to avoid sharevue problems
    // format as date=gtyyyy-mm-dd
    filters.push(`&date=gt${dateStr_gt}`);
    // between 3 months and 1 year ago
    date.setMonth(date.getMonth() - 9);
    let dateStr_le = dateStr_gt;
    dateStr_gt = date.toISOString().slice(0, 19) + 'Z'; // Need the full timezone to avoid sharevue problems
    filters.push(`&date=le${dateStr_le}&date=gt${dateStr_gt}`);
    // Then jump back 1 year at a time for 5 years
    for (let i = 0; i < 5; i++) {
      dateStr_le = dateStr_gt;
      date.setFullYear(date.getFullYear() - 1);
      dateStr_gt = date.toISOString().slice(0, 19) + 'Z'; // Need the full timezone to avoid sharevue problems
      filters.push(`&date=le${dateStr_le}&date=gt${dateStr_gt}`);
    }
    // Now get the rest
    dateStr_le = dateStr_gt;
    filters.push(`&date=le${dateStr_le}`);
  }

  let rawDiagnosticResult = undefined;
  const url = `/fhir/DiagnosticReport?patient=${fhirPatientId}&_elements=${diagnosticReport_elements_str}`;
  let res: IDiagnosticReportSimplified[] | undefined;

  for (const filter of filters) {
    if (signal?.aborted) return undefined;
    // Note that I did add a cb funtion to makePearlApiFhirCallAndUnwrapPaged
    // To let us know when each page of results is fetched.  But because the results are
    // not sorted, they jump around in the ui, so it looks ugly and is not usable.
    // Instead I'm using this approach to grab one year a time, so hopefully each result
    // will fit in a single page of results and won't take too long.
    rawDiagnosticResult = await makePearlApiFhirCallAndUnwrapPaged(
      url + filter,
      mrn,
      server,
      signal,
      undefined, // cb
      newResultsCallback ? undefined : rawDiagnosticResult,
      pageSize
    );
    if ((rawDiagnosticResult?.resourceType as string) === 'OperationOutcome' && (rawDiagnosticResult as any)?.issue) {
      throw new Error(
        'Error from FHIR server when fetching diagnostic reports: ' +
          (rawDiagnosticResult as any).issue[0]?.details?.text || 'Unknown error'
      );
    }
    if (rawDiagnosticResult) {
      // Note that if we're using the cb function, then this is just the newest
      // set of results.
      res = combineDiagnosticReportsWithObservations(rawDiagnosticResult, undefined);
      if (res) newResultsCallback?.(res);
    }
  }
  if (signal?.aborted) return undefined;

  return res;
}

/**
 * Fetch a patient by their FHIR ID.
 * Requires the permissions:
 *   - Patient.Search (Demographics) (R4): api/FHIR/R4/Patient?identifier=<fhirPatientId> - https://fhir.epic.com/Specifications?api=932
 *
 * FIXME: API says:
 *   Patient.$match Alternative: If you need to identify a single patient record, strongly consider using Patient.$match instead.
 *   This is especially useful for backend integrations where sufficient patient demographics are available. Patient.Search
 *   should be used to get a list of patients matching a subset of parameters rather than matching a single patient record.
 *   Improper use of Patient.Search could lead to writing data on the wrong patient record and even patient safety concerns.
 */
export async function fetchPatient(fhirPatientId: string, signal: AbortSignal, server?: string) {
  const fhirConfig: FhirConfigWithHeaders = {
    headers: {
      // we don't have an MRN yet.  Just use the ID instead
      'X-Pearl-MRN': 'id:' + fhirPatientId,
      ...(server ? { 'X-Pearl-FHIR-Servers': server } : {}),
    },
    signal,
  };
  // We can't use makePearlApiFhirCallAndUnwrap because that assumes the correct response is from the first server.
  // If SMART supplied the server, query only that server. Otherwise, query them all and comb through the results.
  // FIXME: Use Patient.$match instead of Patient.Search
  // See https://fhir.epic.com/Specifications?api=932
  const res = await pearlApi.get<{ data: PearlApiPatient & { response: IOperationOutcome }[] }>(
    `/fhir/Patient?identifier=${fhirPatientId}`,
    fhirConfig
  );
  // Note that res.data?.data is an array of objects, one for each server, and make contain the patient or an OperationOutcome error
  // convertPearlApiPatientToIPatient will filter out the correct patient and return it or throw an error if there is only an OperationOutcome error
  return convertPearlApiPatientToIPatient(res.data?.data);
}

export async function fetchPatientByMRN(mrn: string, signal?: AbortSignal) {
  if (!mrn) return Promise.resolve(undefined);
  const fhirConfig: FhirConfigWithHeaders = {
    headers: {
      'X-Pearl-MRN': mrn,
    },
    signal,
  };

  // We can't use makePearlApiFhirCallAndUnwrap because that assumes the correct response is from the first server.
  // But on this first Patient call, we don't know which server the patient is on. So we need to query them all, then
  // comb through the different server results to see which gave us our patient.
  const res = await pearlApi.get<{ data: PearlApiPatient }>(
    `/fhir/Patient?identifier=${encodeURIComponent(mrn)}`,
    fhirConfig
  );
  // Note that res.data?.data is an array of objects, one for each server, and make contain the patient or an OperationOutcome error
  // convertPearlApiPatientToIPatient will filter out the correct patient and return it or throw an error if there is only an OperationOutcome error
  return convertPearlApiPatientToIPatient(res.data?.data, mrn);
}

// Exported for testing
export type PearlApiPatient = {
  server: 'orca' | 'wamt' | 'ak' | 'musc' | 'athena' | 'azure'; // azure is a fake server for demo.  'musc' is only used for musc.
  response: IBundle | IOperationOutcome;
}[];
export type IPatientAndServer = IPatient & { server: PearlApiPatient[number]['server'] };

/** When we call /fhir/Patient it will hopefully return an response with a Bundle which is searchset and contains an entry of type Patient
 *  Return true if a response matches this.  i.e. is not an OperationOutcome and has found a patient.
 *  This is a function just for the "x is" for typescript, but typescript _still_ couldn't figure it out, so kinda useless tbh.
 */
const isValidBundleSearchSetPatientResponse = (x: PearlApiPatient[0], mrn?: string): x is PearlApiPatient[number] => {
  return !!(
    x.response?.resourceType === 'Bundle' &&
    // x.response.total && // athena doesn't have any total field in the response
    // x.response.total > 0 &&
    x.response.type === 'searchset' &&
    x.response.entry &&
    x.response.entry.length > 0 &&
    x.response.entry.some((e) => e.resource?.resourceType === 'Patient')
  );
};

// Exported for testing
export function convertPearlApiPatientToIPatient(
  dataArray: PearlApiPatient,
  mrn?: string
): IPatientAndServer | undefined {
  const matchingServers = dataArray.filter?.((x) => isValidBundleSearchSetPatientResponse(x, mrn));
  if (!matchingServers || matchingServers.length === 0) {
    console.log('Patient not found - Response from fetching patient:', JSON.stringify(dataArray));
    const firstMessage = dataArray[0]?.response as IOperationOutcome;
    if (firstMessage.resourceType === 'OperationOutcome' && firstMessage.issue) {
      const err = firstMessage.issue[0]?.severity + ': Error fetching patient: ' + firstMessage.issue[0]?.details?.text;
      console.error(err);
      snackNotifications.error(err);
    } else {
      console.warn(
        'Patient not found, and no OperationOutcome error message returned from server. Response was:',
        JSON.stringify(dataArray)
      );
      snackNotifications.error('Patient not found');
    }
    return undefined;
  }
  if (matchingServers.length > 1) {
    snackNotifications.error('Patient found in multiple servers - please use a unique MRN');
    return undefined;
  }
  const matchingData = matchingServers[0]!;
  const server = matchingData?.server;
  const data_ = matchingData.response as IBundle;
  // Filter by Patient resource type
  let data = data_.entry!.find((e: any) => e.resource?.resourceType === 'Patient' && getPatientMRN(e.resource, mrn));
  if (!data) {
    console.log('Patient with MRN not found, trying to find any Patient', data_);
    data = data_.entry!.find((e: any) => e.resource?.resourceType === 'Patient');
  }
  if (!data) {
    // Can't happen though
    console.error('Internal bug: Patient not found in response', data_);
    snackNotifications.error('Internal bug: Patient not found');
    return undefined;
  }
  const resource = data.resource;
  return { ...resource, server } as IPatientAndServer;
}

export async function fetchObservationReference(reference: string, mrn: string, server: string, signal?: AbortSignal) {
  const res = await makePearlApiFhirCallAndUnwrap<IObservationSimplified>(`/fhir/${reference}`, mrn, server, signal);
  return res as IObservationSimplified | undefined;
}

export async function fetchDiagnosticReportWithObservations(
  diagnostic_report_id: string,
  mrn: string,
  server: string,
  signal?: AbortSignal
) {
  const res = await makePearlApiFhirCallAndUnwrap<IBundle>(
    `/fhir/DiagnosticReport?_id=${diagnostic_report_id}&_include=DiagnosticReport:result:Observation`,
    mrn,
    server,
    signal
  );
  if (res) {
    return convertRawDiagnosticReportWithObservationsToSimplified(res);
  }
}
export function convertRawDiagnosticReportWithObservationsToSimplified(res: IBundle) {
  const diagnosticReport = res.entry?.find((e) => e.resource?.resourceType === 'DiagnosticReport')?.resource as
    | IDiagnosticReportSimplified
    | undefined;
  const observations = res.entry?.filter((e) => e.resource?.resourceType === 'Observation');
  if (!diagnosticReport) {
    snackNotifications.error('Error from fhir server - Diagnostic report not found');
    return undefined;
  }
  // Combine into a single diagnostic report with the observations in it.
  diagnosticReport.result = (observations?.map((e) => e.resource) || []) as IReference[];

  return diagnosticReport;
}

/** If you add to this, also update convertFhirNetworkPredefinedWidgetToFhirPredefinedWidget
 *  and convertFhirPredefinedWidgetToFhirNetworkPredefinedWidget
 */
export type FhirNetworkPredefinedWidget = Omit<
  FhirPredefinedWidget,
  'rules' | 'subsetOfWidgetIds' | 'almostSubsetOfWidgets'
> & {
  //Note the old format was an array of rules, and we only use the first one.  We convert it to a single rule in @see convertFhirNetworkPredefinedWidgetToFhirPredefinedWidget
  rules: [
    {
      type: 'DiagnosticReportName';
      name: string; // Pipe-separated CPT codes, LOINC codes, or display names. Values are normalized before comparison.
      muscName?: string; // Additional pipe-separated codes or display names for MUSC only.
      hideAllIfNoWhitelistMatches?: string; // e.g. 82570|82043 - i.e. a list of CPT codes
      loincCodes?: string; // e.g. 1234-5|6789-0 - a list of LOINC codes, separated by |.  This is used to filter the observations.
    }
  ];
  privateNotes?: string /* For historical reasons, this needs to be renamed to searchTerms */;
};

export function sanitizeWidgetSearchTerms(searchTerms: string | undefined) {
  if (!searchTerms) return undefined;
  let searchTermsList = searchTerms
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x);
  searchTermsList = sort_unique(searchTermsList, true); // Sort and remove duplicates
  return searchTermsList.join(', ');
}

/** There's a rather specific and odd bug that ckeditor config will choke on a period in an object key.
 *  Easiest way to fix is just to remove all periods from the id.  This is only for the old way we generated
 *  the id from the name.  New widgets have an id that is just a uuid without any periods.
 *
 *  Note this needs to be called when loading the predefined widgets
 * (@see convertFhirNetworkPredefinedWidgetToFhirPredefinedWidget )
 * and in the idsToFhir stored on the page. (@see fixupPageFhir )
 */
export function fixupWidgetId(id: string) {
  return id.replaceAll('.', '');
}

/** rules used to be an array of rules, but now it's just one rule (we just take the first one).
 *
 */
export function convertFhirNetworkPredefinedWidgetToFhirPredefinedWidget(
  widget: FhirNetworkPredefinedWidget
): FhirPredefinedWidget {
  const { privateNotes, ...rest } = widget;
  return {
    ...rest,
    id: fixupWidgetId(widget.id),
    searchTerms: sanitizeWidgetSearchTerms(widget.searchTerms || widget.privateNotes),
    manualSearchTerms: sanitizeWidgetSearchTerms(widget.manualSearchTerms),
    negativeSearchTerms: sanitizeWidgetSearchTerms(widget.negativeSearchTerms),
    rules: widget.rules[0] || {
      type: 'DiagnosticReportName',
      name: '',
      muscName: '',
      hideAllIfNoWhitelistMatches: '',
    },
  };
}

export function convertFhirPredefinedWidgetToFhirNetworkPredefinedWidget(
  widget: FhirPredefinedWidget
): FhirNetworkPredefinedWidget {
  const searchTerms = sanitizeWidgetSearchTerms(widget.searchTerms);
  const manualSearchTerms = sanitizeWidgetSearchTerms(widget.manualSearchTerms);
  const negativeSearchTerms = sanitizeWidgetSearchTerms(widget.negativeSearchTerms);
  const {
    subsetOfWidgetIds,
    almostSubsetOfWidgets,
    index,
    rule,
    muscRule,
    whitelistStr,
    subsetOfWidgetNames,
    ...widgetToSave
  } = widget as FhirPredefinedWidget & {
    index?: number;
    rule?: string;
    muscRule?: string;
    whitelistStr?: string;
    subsetOfWidgetNames?: string;
  };
  return {
    ...widgetToSave,
    searchTerms,
    manualSearchTerms,
    negativeSearchTerms,
    privateNotes: searchTerms,
    rules: [
      {
        type: 'DiagnosticReportName',
        name: widget.rules.name || '',
        muscName: widget.rules.muscName || '',
        ...(widget.rules.loincCodes ? { loincCodes: widget.rules.loincCodes } : {}),
        hideAllIfNoWhitelistMatches: widget.rules.hideAllIfNoWhitelistMatches || '',
      },
    ],
  };
}

export type FhirNetworkOrganizationValuesFhirType = {
  predefined_widgets?: FhirNetworkPredefinedWidget[];
  groupedObservations?: GroupedObservationType[];
};

export function stripTransientPredefinedWidgetFields(widget: FhirPredefinedWidget) {
  delete (widget as any).index;
  delete (widget as any).rule;
  delete (widget as any).muscRule;
  delete (widget as any).whitelistStr;
  delete widget.subsetOfWidgetIds;
  delete widget.almostSubsetOfWidgets;
  delete (widget as any).subsetOfWidgetNames;
  return widget;
}

export async function fetchPredefinedWidgets(orgId: string | undefined): Promise<
  | {
      predefined_widgets: FhirPredefinedWidget[];
      groupedObservations: GroupedObservationType[];
    }
  | undefined
> {
  if (!orgId) return;
  const fhir_values = (
    await pearlApi.get<{ data: FhirNetworkOrganizationValuesFhirType }>(
      `/organizations/${orgId.replaceAll('-', '')}/values/fhir`
    )
  ).data.data;
  const predefined_widgets = cleanupPredefinedWidgets(
    (fhir_values.predefined_widgets || [])
      .map(convertFhirNetworkPredefinedWidgetToFhirPredefinedWidget)
      .map(stripTransientPredefinedWidgetFields)
  );
  // console.log('predefined_widgets', predefined_widgets, fhir_values);
  if (predefined_widgets.length === 0) {
    // This is for initializing the database the very first time
    // thunkAPI.dispatch(updatePredefinedWidgetsThunk(predefinedWidgetOptions));
    return;
  }
  return { groupedObservations: fhir_values?.groupedObservations || [], predefined_widgets };
}

/** Some sanity cleaning of the predefined widgets.
 *   - trim() all the names
 *   - trim() all the titles
 *   - remove any double spaces in rules[x].name and rules[x].muscName
 *   - delete scope and matching from each rule
 *   - remove override title (not used anymore)
 *  Exported only for testing.
 */
export function cleanupPredefinedWidgets(predefined_widgets: FhirPredefinedWidget[]) {
  const cleanedWidgets = predefined_widgets.map((widget) => {
    widget.name = widget.name?.trim();
    widget.title = widget.title?.trim(); // This is the override title, and it is not used anymore
    if (!widget.rules)
      // SHould be impossible
      widget.rules = { type: 'DiagnosticReportName', name: '', muscName: '', hideAllIfNoWhitelistMatches: '' };
    const rule = widget.rules;
    rule.name = rule.name.trim().replace(/\s+/g, ' ');
    // split by | and then sort and remove duplicates
    rule.name = sort_unique(
      rule.name
        .split('|')
        .map((x) => x.trim())
        .filter((x) => x)
    ).join('|');
    if (rule.muscName) {
      rule.muscName = rule.muscName?.trim().replace(/\s+/g, ' ');
      // split by | and then sort and remove duplicates
      rule.muscName = sort_unique(
        rule.muscName
          .split('|')
          .map((x) => x.trim())
          .filter((x) => x)
      ).join('|');
    }
    if (rule.loincCodes) {
      rule.loincCodes = rule.loincCodes?.trim().replace(/\s+/g, ' ');
      // split by | and then sort and remove duplicates
      rule.loincCodes = sort_unique(
        rule.loincCodes
          .split('|')
          .map((x) => x.trim())
          .filter((x) => x)
      ).join('|');
    }
    if (rule.hideAllIfNoWhitelistMatches) {
      rule.hideAllIfNoWhitelistMatches = rule.hideAllIfNoWhitelistMatches.replace(/\s\s+/g, ' ').trim();
      // split by | and then sort and remove duplicates
      rule.hideAllIfNoWhitelistMatches = sort_unique(
        rule.hideAllIfNoWhitelistMatches
          .split('|')
          .map((x) => x.trim())
          .filter((x) => x)
      ).join('|');
    }
    // Try to remove title override
    const titleNoteRegex = /\(([*].*)\)$/;
    const match = widget.title?.match(titleNoteRegex);
    const matchFallback = widget.title?.match(/\((.*)\)$/); // without the *
    if (!widget.title || widget.title?.toLowerCase() === widget.name?.toLowerCase()) {
      // Check if we have a title note starting with '(*' and ending with ')'
      if (match && match[1]) {
        widget.titleNote = match[1].trim();
      }
      delete widget.title;
    } else if (widget.title?.toLowerCase().startsWith(widget.name.toLowerCase())) {
      const titleSuffix = widget.title.slice(widget.name.length).trim();
      if (titleSuffix.startsWith('(') && titleSuffix.endsWith(')')) {
        widget.titleNote = titleSuffix.slice(1, -1).trim();
        delete widget.title;
      }
    } else if (match && match[1]) {
      widget.titleNote = match[1].trim();
      delete widget.title;
    } else if (matchFallback && matchFallback[1]) {
      // Just a general case of something in parentheses at the end
      widget.titleNote = matchFallback[1].trim();
      delete widget.title;
    } else if (widget.name && widget.name.startsWith(widget.title)) {
      // e.g. name = "Zika virus IgM", override title="Zika virus"
      //  or  name = "Complete blood count (for anemia)", override title="Complete blood count"
      // Just remove the title
      delete widget.title;
    } else if (widget.deleted || widget.wip) {
      delete widget.title;
    }
    if (!rule.muscName) delete rule.muscName;
    delete (rule as any).scope;
    delete (rule as any).matching;
    return widget;
  });
  return markPredefinedWidgetRuleSubsets(cleanedWidgets);
}

function getRuleNameParts(widget: FhirPredefinedWidget): string[] {
  return widget.rules?.name
    ? widget.rules.name
        .split('|')
        .map((x) => x.trim())
        .filter((x) => x)
    : [];
}

function isProperSubset(subset: Set<string>, superset: Set<string>) {
  return subset.size > 0 && subset.size < superset.size && Array.from(subset).every((x) => superset.has(x));
}

function getSubsetMatchPercent(subset: Set<string>, superset: Set<string>) {
  if (subset.size === 0 || superset.size === 0) return 0;
  const matchingRuleCount = Array.from(subset).filter((x) => superset.has(x)).length;
  return Math.round((matchingRuleCount / subset.size) * 100);
}

export function markPredefinedWidgetRuleSubsets(predefined_widgets: FhirPredefinedWidget[]) {
  const ruleSetsByWidget = predefined_widgets.map((widget) => ({
    widget,
    ruleSet: new Set(getRuleNameParts(widget)),
  }));

  ruleSetsByWidget.forEach(({ widget, ruleSet }) => {
    const subsetOfWidgetIds = ruleSetsByWidget
      .filter((other) => other.widget.id !== widget.id && isProperSubset(ruleSet, other.ruleSet))
      .map((other) => other.widget.id)
      .sort();

    if (subsetOfWidgetIds.length) {
      widget.subsetOfWidgetIds = subsetOfWidgetIds;
    } else {
      delete widget.subsetOfWidgetIds;
    }

    const almostSubsetOfWidgets = ruleSetsByWidget
      .map((other) => ({
        id: other.widget.id,
        matchPercent: other.widget.id === widget.id ? 0 : getSubsetMatchPercent(ruleSet, other.ruleSet),
        isProperSubset: other.widget.id !== widget.id && isProperSubset(ruleSet, other.ruleSet),
      }))
      .filter((other) => other.matchPercent >= 80 && other.matchPercent < 100 && !other.isProperSubset)
      .sort((a, b) => b.matchPercent - a.matchPercent || a.id.localeCompare(b.id))
      .map(({ id, matchPercent }) => ({ id, matchPercent }));

    if (almostSubsetOfWidgets.length) {
      widget.almostSubsetOfWidgets = almostSubsetOfWidgets;
    } else {
      delete widget.almostSubsetOfWidgets;
    }
  });

  return predefined_widgets;
}

/**
 *  Fetches the patient's medical history
 *
 *  This requires the permissions:
 *  - Condition.Search (Medical History) (R4): /api/FHIR/R4/Condition?category=medical-history - https://fhir.epic.com/Specifications?api=10314
 */
export async function fetchPatientMedicalHx(
  fhirPatientId: string,
  mrn: string,
  server: string,
  signal: AbortSignal,
  newResultsCallback: (medicalHx: ICondition[]) => void
) {
  const medicalHxRaw = await makePearlApiFhirCallAndUnwrap<IConditionBundle>(
    `/fhir/Condition?patient=${fhirPatientId}&category=medical-history`,
    mrn,
    server,
    signal
  );
  if (signal?.aborted) return;
  const medicalHx = medicalHxRaw?.entry
    ?.map((e) => e.resource as ICondition)
    .filter((e) => e?.verificationStatus?.text?.toLowerCase() === 'confirmed');

  console.log('medicalHx', medicalHx, medicalHxRaw);
  if (medicalHx && medicalHx.length > 0) {
    newResultsCallback(medicalHx);
  }
}

/**
 * Refer to Procedure SNOMED code documentation
 * https://fhir.epic.com/Specifications?api=976
 *
 * Permissions required:
 *  - SurgicalProcedure: Procedure.Search (Surgeries) (R4) - https://fhir.epic.com/Specifications?api=10042
 *                     : Procedure.Search (Patient-Reported Surgical History) (R4) - https://fhir.epic.com/Specifications?api=10030
 *  - DiagnosticProcedure: Procedure.Search (Orders) (R4) - https://fhir.epic.com/Specifications?api=976
 */
export const ProcedureCategories = {
  DiagnosticProcedure: '103693007',
  SurgicalProcedure: '387713003', // https://fhir.epic.com/Specifications?api=10042
} as const;

/**
 *  Fetches the patient's surgical history
 *
 *  This requires the permissions:
 *  - SurgicalProcedure: Procedure.Search (Surgeries) (R4) - https://fhir.epic.com/Specifications?api=10042
 *                     : Procedure.Search (Patient-Reported Surgical History) (R4) - https://fhir.epic.com/Specifications?api=10030
 *  - DiagnosticProcedure: Procedure.Search (Orders) (R4) - https://fhir.epic.com/Specifications?api=976
 */
export async function fetchPatientSurgicalHx(
  fhirPatientId: string,
  mrn: string,
  server: string,
  signal: AbortSignal,
  newResultsCallback: (surgicalHx: IProcedure[]) => void
) {
  // As per Epic FHIR documentation, we need to make multiple API calls to get complete list of surgical hx.
  //https://fhir.epic.com/Specifications?api=976
  const diagnosticProcedureRaw = await makePearlApiFhirCallAndUnwrap<IConditionBundle>(
    `/fhir/Procedure?category=${ProcedureCategories.DiagnosticProcedure}&patient=${fhirPatientId}`,
    mrn,
    server,
    signal
  );
  if (signal?.aborted) return;
  const diagnosticProcedure = diagnosticProcedureRaw?.entry
    ?.map((e) => e.resource as IProcedure)
    .filter((e) => e?.status?.toLowerCase() === 'completed');

  console.log('diagnosticProcedure', diagnosticProcedure, diagnosticProcedureRaw);
  if (diagnosticProcedure && diagnosticProcedure.length > 0) {
    newResultsCallback(diagnosticProcedure);
  }

  const surgicalProcedureRaw = await makePearlApiFhirCallAndUnwrap<IConditionBundle>(
    `/fhir/Procedure?category=${ProcedureCategories.SurgicalProcedure}&patient=${fhirPatientId}`,
    mrn,
    server,
    signal
  );
  if (signal?.aborted) return;
  const surgicalProcedure = surgicalProcedureRaw?.entry
    ?.map((e) => e.resource as IProcedure)
    .filter((e) => e?.status?.toLowerCase() === 'completed');

  console.log('surgicalProcedure', surgicalProcedure, surgicalProcedureRaw);
  if (surgicalProcedure && surgicalProcedure.length > 0) {
    newResultsCallback(surgicalProcedure);
  }
}

export async function fetchFhirPatientFromAzure(
  fhirPatient: PatientContextType,
  signal: AbortSignal
): Promise<
  | {
      patient: IPatient | undefined;
      server: string | undefined;
      mrn: string | undefined;
      diagnosticReports?: IDiagnosticReportSimplified[];
      medicines?: IMedicationRequest[];
      vitals?: IObservation[];
      problemList?: ICondition[];
      medicalHx?: ICondition[];
      surgicalHx?: IProcedure[];
    }
  | undefined
> {
  // Fetch from API. These files can be viewed here:
  // https://portal.azure.com/#@medpearl.onmicrosoft.com/resource/subscriptions/3aa3cb4c-e4d0-4711-b90f-a9a95e329ade/resourceGroups/rg-medpearl-dev/providers/Microsoft.Storage/storageAccounts/stmedpearldev01/storagebrowser
  const { data: patientData } = await pearlApi.get<{
    createdAt?: string;
    patient: IPatient;
    diagnosticReports: IDiagnosticReportBundleSimplified;
    medicine: IMedicationRequestBundle | undefined;
    vitals: IObservationBundle | undefined;
    problemList: IConditionBundle | undefined;
    medicalHx: IConditionBundle | undefined;
    surgicalHx: IProcedureBundle | undefined;
  }>(`/test-patients/${fhirPatient.azureFilename}.json`);
  throwIfAborted(signal);
  const shiftedPatientData = adjustTestPatientDates(patientData);
  const {
    patient,
    diagnosticReports: diagnosticReportsAndObservationsBundle,
    medicine: medicineBundle,
    vitals: vitalsBundle,
    problemList: problemListBundle,
    medicalHx: medicalHxBundle,
    surgicalHx: surgicalHxBundle,
  } = shiftedPatientData;
  // Do some sanity checking
  if (patient?.resourceType !== 'Patient') {
    throw new Error(
      `Expected Patient resource from azure file '${fhirPatient.azureFilename}', got:${patient?.resourceType}`
    );
  }
  if (
    diagnosticReportsAndObservationsBundle?.resourceType !== 'Bundle' ||
    diagnosticReportsAndObservationsBundle.type !== 'searchset'
  ) {
    console.error(
      'diagnosticReportsAndObservationsBundle found non-DiagnosticReport at index',
      diagnosticReportsAndObservationsBundle.entry?.findIndex((e) => e.resource?.resourceType !== 'DiagnosticReport')
    );
    throw new Error(
      `Expected array of diagnosticReports resouce from azure file '${fhirPatient.azureFilename}', got:${diagnosticReportsAndObservationsBundle}`
    );
  }
  if (
    medicineBundle &&
    (medicineBundle.resourceType !== 'Bundle' ||
      medicineBundle.type !== 'searchset' ||
      !Array.isArray(medicineBundle.entry))
  ) {
    throw new Error(
      `Expected array of MedicationRequest resource from azure file '${fhirPatient.azureFilename}', got:${medicineBundle}`
    );
  }
  if (
    vitalsBundle &&
    (vitalsBundle.resourceType !== 'Bundle' || vitalsBundle.type !== 'searchset' || !Array.isArray(vitalsBundle.entry))
  ) {
    throw new Error(
      `Expected array of Observation vital resource from azure file '${fhirPatient.azureFilename}', got:${vitalsBundle}`
    );
  }
  const patientProblemListAndPastMedicalHxEnabled = showPatientProblemListAndPastMedicalHx();
  if (
    patientProblemListAndPastMedicalHxEnabled &&
    problemListBundle &&
    (problemListBundle.resourceType !== 'Bundle' ||
      problemListBundle.type !== 'searchset' ||
      !Array.isArray(problemListBundle.entry))
  ) {
    throw new Error(
      `Expected array of Condition problemList resource from azure file '${fhirPatient.azureFilename}', got:${problemListBundle}`
    );
  }
  if (
    medicalHxBundle &&
    (medicalHxBundle.resourceType !== 'Bundle' ||
      medicalHxBundle.type !== 'searchset' ||
      !Array.isArray(medicalHxBundle.entry))
  ) {
    throw new Error(
      `Expected array of MedicalHx resource from azure file '${fhirPatient.azureFilename}', got:${medicalHxBundle}`
    );
  }
  if (
    surgicalHxBundle &&
    (surgicalHxBundle.resourceType !== 'Bundle' ||
      surgicalHxBundle.type !== 'searchset' ||
      !Array.isArray(surgicalHxBundle.entry))
  ) {
    throw new Error(
      `Expected array of SurgicalHx resource from azure file '${fhirPatient.azureFilename}', got:${surgicalHxBundle}`
    );
  }
  const diagnosticReports = combineDiagnosticReportsWithObservations(
    diagnosticReportsAndObservationsBundle,
    diagnosticReportsAndObservationsBundle
  );
  const medicines = medicineBundle?.entry.map((e) => e.resource as IMedicationRequest);
  const vitals = vitalsBundle?.entry.map((e) => e.resource as IObservation);
  const problemList = patientProblemListAndPastMedicalHxEnabled
    ? problemListBundle?.entry.map((e) => e.resource as ICondition)
    : undefined;
  const medicalHx = medicalHxBundle?.entry.map((e) => e.resource as ICondition);
  const surgicalHx = surgicalHxBundle?.entry.map((e) => e.resource as IProcedure);
  const mrn = undefined;
  return {
    patient,
    server: 'https://fakefhirserver.example.com',
    mrn,
    diagnosticReports,
    medicines,
    vitals,
    medicalHx,
    problemList,
    surgicalHx,
  };
}

export function throwIfAborted(signal: AbortSignal | undefined) {
  if (signal?.aborted) {
    throw new Error('Operation cancelled');
  }
}

/**
 * Set to true to use POST for batch requests, false to use GET with _id=1,2,3
 * Using POST is faster for large numbers of requests, but for providence
 * we don't seem to support it - it gives an error:
 *   "Http/1.1 Service Unavailable"
 */
const USE_POST_FOR_BATCH_REQUESTS = false;

export async function fetchFhirBatchRequest(
  urls: FetchedBinaryType[],
  mrn: string,
  server: string,
  signal?: AbortSignal,
  onProgress?: (binaries: FetchedBinaryType[]) => void
) {
  if (!urls || urls.length === 0) {
    return Promise.resolve(undefined);
  }
  if (USE_POST_FOR_BATCH_REQUESTS) {
    // Use POST for batch requests
    const bundleRequest = {
      resourceType: 'Bundle',
      type: 'batch',
      entry: urls.map((url) => ({
        request: {
          method: 'GET',
          url: url.url,
        },
      })),
    };
    await makePearlApiFhirPostCallAndUnwrap<IBundle>(`/fhir/`, bundleRequest, mrn, server, signal);
    throw new Error('Not implemented yet');
  } else {
    // Use GET
    const results: FetchedBinaryType[] = [];
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i]!;
      throwIfAborted(signal);
      const res = await makePearlApiFhirCallAndUnwrap<string>(`/fhir/${url.url}`, mrn, server, signal);
      if (res) {
        results.push({ ...url, content: res });
      }
      onProgress?.(results);
    }
    return results;
  }
}

export type FetchedBinaryType = { url: string; content?: string; contentType?: string; resource?: IDocumentReference };

/**
 * When you fetch the clinical, e.g.:
 *    DocumentReference?patient=eINzXLrm1gIrl8Wa7MsXzkA3&category=clinical-note
 *  You get back a bundle of DocumentReference resources, each of which has a content.attachment.url
 * This function will fetch the actual content of those urls.
 * In FhirDebug we display the content of these inline.
 */
export async function fetchAllBinariesInBundle(
  bundles: IBundle_Entry[],
  max: number,
  preferredMimeType: string[] = ['text/plain', 'text/html'],
  server: string,
  mrn?: string,
  signal?: AbortSignal,
  onProgress?: (binaries: FetchedBinaryType[]) => void
): Promise<FetchedBinaryType[] | undefined> {
  const binaries_urls: FetchedBinaryType[] = bundles
    .flatMap((entry) => {
      if (entry.resource?.resourceType === 'DocumentReference') {
        if (preferredMimeType.length === 0) {
          // Get all
          return (
            entry.resource.content?.map((c) => ({
              url: c.attachment?.url!,
              contentType: c.attachment?.contentType!,
              resource: entry.resource as IDocumentReference,
            })) || []
          );
        } else {
          // Look for mime types in order of preference
          for (const mimeType of preferredMimeType) {
            const url = entry.resource.content?.filter((c) => c.attachment?.contentType === mimeType)?.[0]?.attachment
              ?.url;
            if (url) return [{ url: url!, contentType: mimeType!, resource: entry.resource as IDocumentReference }];
          }
          return [];
        }
      }
      return [];
    })
    .filter((binary) => !!binary && !!binary.url)
    .slice(0, max);
  if (binaries_urls.length === 0) {
    snackNotifications.info('No binaries found in this result');
    return;
  }
  let binaries: FetchedBinaryType[] | undefined = undefined;
  try {
    binaries = await fetchFhirBatchRequest(binaries_urls, mrn || 'testing_fake_mrn', server || '*', signal, onProgress);
  } catch (e) {
    snackNotifications.error('Error fetching binaries: ' + (e as any).toString());
  }
  return binaries;
}
